import {
  AlertTriangle,
  CalendarRange,
  Download,
  LoaderCircle,
  MapPinned,
  RefreshCw,
  Table2,
} from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { useCallback, useEffect, useState } from 'react';
import { ReportDataWarnings } from '../../components/ReportDataWarnings';
import { requireFirebaseFunctions } from '../../lib/firebase';
import { formatReportGeneration, OUTPUT_REPORT_CALLABLE_OPTIONS } from '../../lib/generated-report';
import {
  outputReportMonths,
  outputReportYears,
  type ProcessingAvailability,
} from '../../lib/processing-data';
import {
  createTerritorialAnalysisWorkbook,
  territorialAnalysisWorkbookFileName,
  type TerritorialAnalysis,
  type TerritorialMarketTable,
  type TerritorialMetric,
  type TerritorialTrend,
} from '../../lib/territorial-analysis';
import './TerritorialAnalysisPanel.css';

const MONTHS = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre',
];

const DEMO_METRICS: TerritorialMetric[] = [
  { key: 'incomplete-activation', label: 'Attivazione non completata' },
  { key: 'uncabled-street-number', label: 'Civico non cablato' },
  { key: 'missed-appointment-delivery', label: 'Mancato appuntamento - Delivery' },
  { key: 'missed-appointment-assurance', label: 'Mancato appuntamento - Assurance' },
  { key: 'appointment-request-change', label: 'Richiesta/spostamento di appuntamento' },
  { key: 'maintenance-assurance', label: 'Manutenzione - Assurance' },
  { key: 'damage', label: 'Danni' },
  { key: 'poor-work', label: 'Lavori fatti male' },
  { key: 'network-connection-missing', label: 'Mancata connessione rete' },
  { key: 'road-surface-restoration', label: 'Ripristino manto stradale' },
  { key: 'never-contacted', label: 'Mai contattato*' },
];

const DEMO_AREAS = [
  'Abruzzo - Molise', 'Campania - Basilicata', 'Calabria', 'Emilia Romagna',
  'Trentino Alto Adige - Friuli Venezia Giulia', 'Lazio', 'Piemonte Sud - Liguria',
  'Lombardia Est', 'Lombardia Ovest', 'Umbria - Marche',
  "Piemonte Nord - Valle d'Aosta", 'Puglia', 'Sardegna', 'Sicilia', 'Toscana',
  'Veneto', 'Roma - Lazio',
];

type LoadStatus = 'idle' | 'loading' | 'ready' | 'error';

function formatNumber(value: number): string {
  return new Intl.NumberFormat('it-IT').format(value);
}

function defaultMonthForYear(year: number): number {
  const now = new Date();
  return year === now.getFullYear() ? Math.max(1, now.getMonth()) : 12;
}

function errorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
    return error.message.replace(/^Firebase:\s*/i, '').replace(/\s*\([^)]*\)\.?$/, '');
  }
  return 'Non è stato possibile generare l’analisi territoriale.';
}

function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function demoMarket(key: 'residential' | 'business-pa', multiplier: number): TerritorialMarketTable {
  const rows = DEMO_AREAS.map((label, rowIndex) => {
    const values = DEMO_METRICS.map((_, metricIndex) => ((rowIndex + 2) * (metricIndex + 3) * multiplier) % 19);
    const previousValues = values.map((value, metricIndex) => Math.max(0, value + ((rowIndex + metricIndex) % 3) - 1));
    const trends = values.map((value, metricIndex): TerritorialTrend => (
      value < previousValues[metricIndex] ? 'down' : value > previousValues[metricIndex] ? 'up' : 'same'
    ));
    return { key: `area-${rowIndex + 1}`, label, values, previousValues, trends };
  });
  const totals = DEMO_METRICS.map((_, index) => rows.reduce((sum, row) => sum + row.values[index], 0));
  const previousTotals = DEMO_METRICS.map((_, index) => rows.reduce((sum, row) => sum + row.previousValues[index], 0));
  return {
    key,
    label: key === 'residential' ? 'Analisi mercato residenziale' : 'Analisi mercato business e PA',
    customerTypeLabel: key === 'residential' ? 'Residenziale' : 'Business e Public Administration',
    sourceRows: rows.reduce((sum, row) => sum + row.values.reduce((total, value) => total + value, 0), 0),
    unresolvedRows: 0,
    rows,
    totals: {
      values: totals,
      previousValues: previousTotals,
      trends: totals.map((value, index) => value < previousTotals[index] ? 'down' : value > previousTotals[index] ? 'up' : 'same'),
    },
  };
}

function demoAnalysis(year: number, month: number): TerritorialAnalysis {
  const previousMonth = month === 1 ? 12 : month - 1;
  const previousYear = month === 1 ? year - 1 : year;
  return {
    selectedYear: year,
    selectedMonth: month,
    periodLabel: `${MONTHS[month - 1]} ${year}`,
    previousYear,
    previousMonth,
    previousPeriodLabel: `${MONTHS[previousMonth - 1]} ${previousYear}`,
    topicLabel: 'Problemi e Reclami / Solleciti',
    metrics: DEMO_METRICS,
    markets: [demoMarket('residential', 3), demoMarket('business-pa', 1)],
    masterFiles: [{ id: 'demo-master', name: `${year}.xlsx`, path: `Export / Export annuale / ${year}.xlsx`, modifiedTime: null, webViewLink: null }],
    worksheetName: 'Report Segnalazioni Utenti Soci',
  };
}

function trendDescription(trend: TerritorialTrend): string {
  if (trend === 'down') return 'in diminuzione';
  if (trend === 'up') return 'in aumento';
  return 'invariato';
}

function MarketTable({ analysis, market }: { analysis: TerritorialAnalysis; market: TerritorialMarketTable }) {
  const marketScope = market.key === 'residential' ? 'Mercato Residenziale' : 'Mercato Business e PA';
  return (
    <section className="territorial-analysis-card" aria-labelledby={`territorial-market-${market.key}`}>
      <header>
        <div>
          <p>Tabella territoriale</p>
          <h2 id={`territorial-market-${market.key}`}>{market.label}</h2>
          <span>Tipo cliente: {market.customerTypeLabel}. Confronto con {analysis.previousPeriodLabel}.</span>
        </div>
        <div className="territorial-analysis-market-summary">
          <span><strong>{formatNumber(market.sourceRows)}</strong> righe nel perimetro</span>
          {market.unresolvedRows > 0 && <span className="warning"><strong>{formatNumber(market.unresolvedRows)}</strong> senza area</span>}
        </div>
      </header>
      <div className="territorial-analysis-scroll">
        <table className="territorial-analysis-table">
          <thead>
            <tr className="territorial-analysis-group-row">
              <th scope="col">Code</th>
              <th scope="colgroup" colSpan={analysis.metrics.length}>
                Social Subreason (Social Reason analizzata: Problemi e Reclami e Solleciti) {marketScope}
              </th>
            </tr>
            <tr className="territorial-analysis-metric-row">
              <th scope="col"><span>Area territoriale</span></th>
              {analysis.metrics.map((metric) => <th scope="col" key={metric.key}><span>{metric.label}</span></th>)}
            </tr>
          </thead>
          <tbody>
            {market.rows.map((row) => (
              <tr key={row.key}>
                <th scope="row">{row.label}</th>
                {row.values.map((value, index) => {
                  const cellTrend = row.trends[index] ?? 'same';
                  const previous = row.previousValues[index] ?? 0;
                  return (
                    <td
                      key={analysis.metrics[index]?.key ?? index}
                      className={`trend-${cellTrend}`}
                      title={`${analysis.periodLabel}: ${formatNumber(value)} · ${analysis.previousPeriodLabel}: ${formatNumber(previous)} · ${trendDescription(cellTrend)}`}
                      aria-label={`${formatNumber(value)}, ${trendDescription(cellTrend)} rispetto a ${formatNumber(previous)}`}
                    >
                      {formatNumber(value)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th scope="row">Totale</th>
              {market.totals.values.map((value, index) => <td key={analysis.metrics[index]?.key ?? index}>{formatNumber(value)}</td>)}
            </tr>
          </tfoot>
        </table>
      </div>
      <footer><Table2 size={14} /> {market.rows.length} aree territoriali · {analysis.metrics.length} indicatori · nessuna paginazione</footer>
    </section>
  );
}

export function TerritorialAnalysisPanel({ availability, demo }: { availability: ProcessingAvailability | null; demo: boolean }) {
  const [year, setYear] = useState<number | ''>('');
  const [month, setMonth] = useState<number | ''>('');
  const [analysis, setAnalysis] = useState<TerritorialAnalysis | null>(null);
  const [status, setStatus] = useState<LoadStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  const availableYears = outputReportYears(availability, 'territorial-analysis');
  const selectedYear = year;
  const availableMonths = selectedYear
    ? outputReportMonths(availability, 'territorial-analysis', selectedYear)
    : [];
  const selectedMonth = month;

  useEffect(() => {
    if (year || availableYears.length === 0) return;
    const initialYear = availableYears[0];
    const timeout = window.setTimeout(() => {
      setYear(initialYear);
      setMonth(outputReportMonths(availability, 'territorial-analysis', initialYear).at(-1) ?? defaultMonthForYear(initialYear));
    }, 0);
    return () => window.clearTimeout(timeout);
  }, [availability, availableYears, year]);

  const loadAnalysis = useCallback(async (regenerate = false) => {
    if (!selectedYear || !selectedMonth) return;
    setStatus('loading');
    setError(null);
    setExportError(null);
    try {
      const result = demo
        ? {
            ...demoAnalysis(selectedYear, selectedMonth),
            generation: {
              generatedAt: new Date().toISOString(),
              source: regenerate ? 'manual' as const : 'nightly' as const,
              cached: !regenerate,
              version: 8,
            },
          }
        : (await httpsCallable<{ year: number; month: number; regenerate: boolean }, TerritorialAnalysis>(
            requireFirebaseFunctions(),
            'getOutputTerritorialAnalysisReport',
            OUTPUT_REPORT_CALLABLE_OPTIONS,
          )({ year: selectedYear, month: selectedMonth, regenerate })).data;
      setAnalysis(result);
      setStatus('ready');
    } catch (cause) {
      setError(errorMessage(cause));
      setStatus('error');
    }
  }, [demo, selectedMonth, selectedYear]);

  useEffect(() => {
    if (!selectedYear || !selectedMonth || status !== 'idle') return;
    const timeout = window.setTimeout(() => void loadAnalysis(false), 0);
    return () => window.clearTimeout(timeout);
  }, [loadAnalysis, selectedMonth, selectedYear, status]);

  const exportExcel = () => {
    if (!analysis) return;
    setExportError(null);
    try {
      const workbook = createTerritorialAnalysisWorkbook(analysis);
      downloadBlob(
        new Blob([Uint8Array.from(workbook).buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
        territorialAnalysisWorkbookFileName(analysis.selectedYear, analysis.selectedMonth),
      );
    } catch {
      setExportError('Non è stato possibile creare il file Excel.');
    }
  };

  const residential = analysis?.markets.find(({ key }) => key === 'residential');
  const business = analysis?.markets.find(({ key }) => key === 'business-pa');

  return (
    <>
      <section className="trend-period-card">
        <div className="trend-period-copy">
          <span><CalendarRange size={22} /></span>
          <div>
            <p>Periodo di analisi</p>
            <h2>Seleziona anno e mese</h2>
            <small>Il report legge il Master e confronta ogni valore con il mese precedente.</small>
          </div>
        </div>
        <div className="trend-period-actions">
          <label>
            <span>Anno</span>
            <select value={selectedYear} disabled={!availability || status === 'loading'} onChange={(event) => {
              const nextYear = Number(event.target.value);
              setYear(nextYear);
              setMonth(outputReportMonths(availability, 'territorial-analysis', nextYear).at(-1) ?? defaultMonthForYear(nextYear));
              setAnalysis(null);
              setStatus('idle');
            }}>
              {!availableYears.length && <option value="">Caricamento…</option>}
              {availableYears.map((availableYear) => <option value={availableYear} key={availableYear}>{availableYear}</option>)}
            </select>
          </label>
          <label>
            <span>Mese</span>
            <select value={selectedMonth} disabled={!selectedYear || status === 'loading'} onChange={(event) => {
              setMonth(Number(event.target.value));
              setAnalysis(null);
              setStatus('idle');
            }}>
              {!selectedMonth && <option value="">Seleziona</option>}
              {availableMonths.map((availableMonth) => (
                <option value={availableMonth} key={availableMonth}>{MONTHS[availableMonth - 1]}</option>
              ))}
            </select>
          </label>
          <button className="button button-primary" type="button" onClick={() => void loadAnalysis(analysis !== null)} disabled={!selectedYear || !selectedMonth || status === 'loading'}>
            {status === 'loading' ? <LoaderCircle className="spin" size={17} /> : <MapPinned size={17} />}
            {status === 'loading' ? 'Elaborazione…' : analysis ? 'Rigenera report' : 'Genera report'}
          </button>
        </div>
      </section>

      {status === 'idle' && (
        <section className="trend-empty-state">
          <span><MapPinned size={24} /></span>
          <div><strong>Il report è pronto per essere generato</strong><p>Scegli il periodo per confrontare mercato residenziale e Business/PA.</p></div>
        </section>
      )}
      {status === 'loading' && (
        <section className="trend-empty-state loading">
          <span><LoaderCircle className="spin" size={24} /></span>
          <div><strong>Sto aggregando il Master per provincia</strong><p>Applico tipo cliente, argomento, Social Reason e Social Subreason.</p></div>
        </section>
      )}
      {status === 'error' && (
        <section className="trend-empty-state error">
          <span><AlertTriangle size={24} /></span>
          <div><strong>Report non disponibile</strong><p>{error}</p></div>
          <button type="button" className="button button-secondary" onClick={() => void loadAnalysis(false)}><RefreshCw size={16} /> Riprova</button>
        </section>
      )}

      {status === 'ready' && analysis && residential && business && (
        <>
          <section className="trend-summary-grid" aria-label="Sintesi Analisi territoriale">
            <article><span>Periodo</span><strong>{analysis.periodLabel}</strong><small>confrontato con {analysis.previousPeriodLabel}</small></article>
            <article><span>Aree territoriali</span><strong>{formatNumber(residential.rows.length)}</strong><small>configurazione delle code per provincia</small></article>
            <article><span>Residenziale</span><strong>{formatNumber(residential.sourceRows)}</strong><small>righe Problemi e Reclami / Solleciti</small></article>
            <article><span>Business e PA</span><strong>{formatNumber(business.sourceRows)}</strong><small>Business + Public Administration</small></article>
          </section>

          {analysis.generation && (
            <div className="report-generation-note" role="status">
              <RefreshCw size={15} />
              <span>{formatReportGeneration(analysis.generation)}. Il pulsante “Rigenera report” sovrascrive questa versione.</span>
            </div>
          )}

          <ReportDataWarnings warnings={analysis.dataWarnings} />

          <section className="territorial-analysis-toolbar">
            <div>
              <strong>Confronto mensile</strong>
              <span className="trend-down"><i /> verde: diminuzione</span>
              <span className="trend-up"><i /> rosso: aumento</span>
              <span className="trend-same"><i /> nero: invariato</span>
            </div>
            <div className="territorial-analysis-files">
              {analysis.masterFiles.map((file) => <span key={file.id}>{file.name}</span>)}
            </div>
            <button type="button" className="button button-primary" onClick={exportExcel}><Download size={15} /> Esporta Excel</button>
          </section>

          <MarketTable analysis={analysis} market={residential} />
          <MarketTable analysis={analysis} market={business} />
          {exportError && <div className="trend-export-error" role="alert"><AlertTriangle size={15} /> {exportError}</div>}
        </>
      )}
    </>
  );
}
